//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { deleteLabel } from '@apis/gatewayMediaApi';
import { ISearchLabelsResponse } from '@apis/gatewayMediaApiModel';
import {
  labelsQueryKeys,
  manageLabelsQueryKeys,
} from '@content-drawer/contentDrawerConstants';
import { checkIsLabelUpdatedOrDeleted } from '@content-drawer/filters/labels/utils/checkIsLabelUpdatedOrDeleted';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';

export const useDeleteLabelMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const mutationFn = async (label: string) => {
    const instanceId = await deleteLabel(label);
    return checkIsLabelUpdatedOrDeleted({ instanceId, label });
  };

  return useMutation({
    mutationFn,
    onMutate: (label) => {
      // Optimistically delete label
      queryClient.setQueryData(
        labelsQueryKeys,
        (oldData?: ISearchLabelsResponse) => {
          const count = (oldData?.data.count ?? 1) - 1;
          const indexObjects = (oldData?.data.indexObjects ?? []).filter(
            (indexObject) => indexObject.userTag !== label,
          );
          return { data: { count, indexObjects } };
        },
      );
    },
    onSettled: async (succeed, error, label) => {
      if (!succeed || error) {
        await queryClient.invalidateQueries({
          queryKey: manageLabelsQueryKeys,
        });
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Label Remove Failed',
            message: `Unable to remove the label "${label}". Please try again or contact Bazaarvoice Support.`,
            type: ToastType.ERROR,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    },
  });
};
